<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Email Template'"
        :baseView="baseView"
        :primaryLabel="getPrimaryLabel"
        :secondaryLabel="getSecondaryLabel"
        v-if="loaded"
      />
    </template>

    <div class="email-template css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="model"
          :itemSaveCallBack="saveTemplate"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          :baseView="baseView"
          :key="toolBarKey"
        ></gc-form-header-toolbar>
      </div>

      <div class="et-body">
        <!-- Row 1 (2/3 height): Subject/Body card + Live Preview -->
        <div class="et-row-main" :style="{ height: topRowHeight + 'px' }">
          <div class="et-two-col">
            <!-- Left card: template fields (read-only in View, editable in Create/Edit) -->
            <DxValidationGroup ref="validationGroup">
              <div class="et-card">
                <div ref="fields-above-body">
                  <div class="et-field" ref="subject-field">
                    <label>Subject</label>
                    <div class="et-value" v-if="!editable">
                      {{ model.Subject }}
                    </div>
                    <DxTextBox
                      v-else
                      :value="model.Subject"
                      :max-length="255"
                      @value-changed="(e) => setField('Subject', e.value)"
                      @focus-in="lastFocusedField = 'Subject'"
                      ref="subject-textbox"
                    >
                      <DxValidator validationMessageMode="always">
                        <DxRequiredRule message="Subject is required" />
                      </DxValidator>
                    </DxTextBox>
                  </div>
                  <div class="et-field" ref="filename-field">
                    <label>Filename</label>
                    <div class="et-value" v-if="!editable">
                      {{ model.FileNameTemplate }}
                    </div>
                    <DxTextBox
                      v-else
                      :value="model.FileNameTemplate"
                      :max-length="255"
                      @value-changed="(e) => setField('FileNameTemplate', e.value)"
                      @focus-in="lastFocusedField = 'Filename'"
                      ref="filename-textbox"
                    >
                      <DxValidator validationMessageMode="always">
                        <DxRequiredRule message="Filename is required" />
                      </DxValidator>
                    </DxTextBox>
                  </div>
                </div>
                <div class="et-field et-field-grow">
                  <label>Body</label>
                  <gc-html-editor
                    :value="model.Body"
                    :read-only="!editable"
                    :height="bodyMaxHeight + 'px'"
                    @value-changed="(e) => setField('Body', e.value)"
                    @focus-in="lastFocusedField = 'Body'"
                    ref="body-editor"
                  >
                    <DxValidator v-if="editable" validationMessageMode="always">
                      <DxCustomRule
                        :validation-callback="validateBodyRequired"
                        message="Body is required"
                      />
                    </DxValidator>
                  </gc-html-editor>
                </div>
              </div>
            </DxValidationGroup>

            <!-- Right column: live preview -->
            <div class="et-right-col">
              <div class="et-preview-pane">
                <div class="et-preview-label">Live Preview</div>
                <div class="et-preview-subject" v-html="previewSubject"></div>
                <div class="et-preview-filename" v-html="previewFileName"></div>
                <div class="et-preview-body" v-html="previewBody"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 2 (1/3 height): Placeholders / Version History -->
        <div
          class="et-row-secondary"
          :style="{ height: bottomRowHeight + 'px' }"
        >
          <DxTabs
            :data-source="getTabItems"
            :selected-index="activeTabIndex"
            :on-item-click="onTabItemClick"
          />

          <div
            v-show="activeTabIndex === 0"
            class="et-tab-panel placeholder-tab"
          >
            <div
              v-for="group in placeholderGroups"
              :key="group.name"
              class="et-ph-group"
            >
              <div class="et-ph-group-title">{{ group.name }}</div>
              <div class="et-ph-chips">
                <span
                  v-for="ph in group.items"
                  :key="ph.PlaceholderKey"
                  class="et-ph-chip"
                  :class="{ 'et-ph-chip-clickable': editable }"
                  :title="ph.Label"
                  @click="editable && insertPlaceholder(ph.PlaceholderKey)"
                  >{{ placeholderToken(ph.PlaceholderKey) }}</span
                >
              </div>
            </div>
          </div>

          <div v-show="activeTabIndex === 1" class="et-tab-panel">
            <dx-data-grid
              :data-source="versionHistoryDataSource"
              :columns="versionHistoryColumns"
              :show-borders="true"
              :focused-row-enabled="false"
              :column-chooser="{ enabled: false }"
              :pager="{ visible: false }"
              :height="getGridHeight"
              ref="version-history-grid"
            >
              <DxPaging :enabled="false" />
              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preloadEnabled="true"
              />
            </dx-data-grid>
          </div>
        </div>
      </div>
    </div>
  </GcBaseLayout>
</template>

<script>
import { DxValidationGroup } from "devextreme-vue/validation-group";
import {
  DxValidator,
  DxRequiredRule,
  DxCustomRule,
} from "devextreme-vue/validator";

import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";

import { FeatureStorage } from "@/_services/storage";
import { EmailTemplateService } from "../../../_services/api/settings/graycorp/email-template.service";
import GcHtmlEditor from "@/components/common/gc-html-editor";
import { mapGetters } from "vuex";
import validationEngine from "devextreme/ui/validation_engine";

export default {
  mixins: [RouteMixin, FormMixin],
  components: {
    DxValidationGroup,
    DxValidator,
    DxRequiredRule,
    DxCustomRule,
    GcHtmlEditor,
  },
  data() {
    return {
      loaded: false,
      backButtonRoute: "/email-template",
      activeTabIndex: 0,
      lastFocusedField: "Subject",
      tabItems: [{ text: "Placeholders" }, { text: "Version History" }],
      placeholderDefinitions: [],
      documentTypeOptions: [],
      model: {
        RecordId: undefined,
        Name: "",
        EmailTemplateDocumentTypeRecordID: undefined,
        EmailTemplateDocumentType: "",
        Subject: "",
        FileNameTemplate: "",
        Body: "",
        IsDefault: true,
        IsActive: true,
        Versions: [],
        FriendlyStatus: undefined,
        DefaultLabel: "Default",
        CreatedBy: undefined,
        DateCreated: undefined,
        ChangedBy: undefined,
        DateChanged: undefined,
      },
      toolbarHeight: 0,
      aboveBodyFieldsHeight: 0,
      toolBarKey: 0,
    };
  },
  computed: {
    getTabItems() {
      return [
        { text: "Placeholders" },
        { text: "Version History", disabled: this.baseView.mode === "create" },
      ];
    },
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    /**
     * Space below this screen's own toolbar (now taller — it embeds the
     * Name/Document Type/Default/Active fields), split 2/3 for the
     * Subject/Body + Live Preview row and 1/3 for the Placeholders/Version
     * History row below it.
     */
    bodyHeight() {
      // Clamped — an unpopulated gridHeight (Vuex) must never produce a
      // negative/zero row height: that's invalid CSS, gets silently
      // dropped, and the rows fall back to auto (grow with content).
      return Math.max(this.gridHeight - this.toolbarHeight - 20, 300); // 20 => row gap/padding
    },
    topRowHeight() {
      return Math.round((this.bodyHeight * 2) / 3);
    },
    bottomRowHeight() {
      return this.bodyHeight - this.topRowHeight - 12;
    },
    // Reactive to topRowHeight/aboveBodyFieldsHeight only — never to Body's own
    // content — so the editor doesn't visually resize while the user types.
    bodyMaxHeight() {
      return Math.max(this.topRowHeight - this.aboveBodyFieldsHeight - 32 - 50, 100);
    },
    /**
     * Computed property to calculate the height of the data grid
     * @returns {number} The height of the data grid
     */
    getGridHeight() {
      return Math.max(this.bottomRowHeight - 40, 100); // 50 => DxTabs bar + tab-panel padding
    },
    editable() {
      return this.baseView.mode === "create" || this.baseView.mode === "edit";
    },
    getPrimaryLabel() {
      if (this.baseView.mode === "create") return "Create Email Template";
      return this.model.Name;
    },
    getSecondaryLabel() {
      if (this.baseView.mode === "create") return undefined;
      return this.model.EmailTemplateDocumentType;
    },
    getHeaderToolbar() {
      let IsActive = false;
      if (this.baseView.mode === "review") {
        IsActive = { ...this.model.IsActive };
      }
      return {
        audit: true,
        status: this.baseView.mode == "review",
        // customStatus: {
        //   enabled:
        //     this.model.IsDefault === "Y" && this.baseView.mode === "review",
        //   key: "DefaultLabel",
        // },
        save: true,
        editVisible: FeatureStorage.checkPermission("EmailTemplate", "edit"),
        editURL: "/email-template",
        editCallBack: () => {
          this.focusNameField();
        },
        ellipsis: {
          visible:
            this.baseView.mode === "review" &&
            FeatureStorage.checkPermission("EmailTemplate", "edit"),
          menuItems: [
            {
              menuText: "Reactivate",
              visible: !IsActive,
            },
            {
              menuText: "Inactivate",
              visible: IsActive,
            },
          ],

          // eslint-disable-next-line no-unused-vars
          clickAction: (e) => {
            if (e.itemData.menuText === "Inactivate") this.inactivateTemplate();
            else if (e.itemData.menuText === "Reactivate")
              this.reactivateTemplate();
          },
        },
        formContentReady: (e) => {
          this.toolbarformContentReady(e);
        },
        toolbarFormItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            items: [
              {
                screenField: "Name",
                dataField: "Name",
                label: {
                  text: "Template Name",
                  visible: false,
                },
                editorOptions: {
                  placeholder: "Enter template name",
                  width: "275",
                  maxLength: 255,
                  onFocusIn: (e) => {
                    e.event.target.select();
                  },
                },
                required: true,
              },
              {
                screenField: "EmailTemplateDocumentType",
                dataField: "EmailTemplateDocumentTypeRecordID",
                // isPrimaryKeyField opts out of gc-form.vue's automatic
                // remote-DataSource wiring (getGlobal()) — that wiring
                // constructs a live devextreme DataSource/ODataStore
                // instance nested inside this reactively-observed
                // toolbarFormItems tree, and its own internal state
                // mutations were feeding back into Vue's reactivity,
                // causing the "infinite update loop" warning. We already
                // have the full option list in documentTypeOptions, so
                // bind that plain array directly instead.
                isPrimaryKeyField: true,
                editorType: "dxSelectBox",
                label: {
                  text: "Document / Transaction Type",
                  visible: false,
                },
                editorOptions: {
                  placeholder: "Select document type",
                  width: "200",
                  items: this.documentTypeOptions,
                  valueExpr: "RecordId",
                  displayExpr: "Label",
                  searchEnabled: true,
                  searchExpr: "Label",
                  searchMode: "contains",
                  onValueChanged: (e) => {
                    this.onDocumentTypeChanged(e);
                  },
                },
                required: true,
              },
            ],
          },
        ],
        toolbarAfterFormItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            cssClass:
              this.baseView.mode != "review"
                ? "gc-form-header-toolbar-after-group-editor"
                : "gc-form-header-toolbar-after-group",
            items: [
              {
                screenField: "IsDefault",
                dataField: "IsDefault",
                label: {
                  text: "Default",
                },
                editorOptions: {
                  onValueChanged: (e) => {
                    this.setField("IsDefault", e.value);
                  },
                },
              },
              {
                screenField: "Inactive",
                editorType: "dxSwitch",
                dataField: "IsActive",
                cssClass: "field-checkbox-active",
                label: {
                  text: "Active",
                },
                editorOptions: {
                  onValueChanged: (e) => {
                    this.setField("IsActive", e.value);
                  },
                },
                visible: this.baseView.mode != "review",
              },
            ],
          },
        ],
      };
    },
    placeholderGroups() {
      const groups = [];
      // Defensive backstop (Bug 77364) — sort by Sequence before bucketing
      // so both group order (by each group's first/lowest-Sequence member)
      // and item order within a group stay deterministic even if the API
      // response order ever changes independently of this.
      const sorted = [...this.placeholderDefinitions].sort(
        (a, b) => a.Sequence - b.Sequence,
      );
      sorted.forEach((ph) => {
        let group = groups.find((g) => g.name === ph.GroupName);
        if (!group) {
          group = { name: ph.GroupName, items: [] };
          groups.push(group);
        }
        group.items.push(ph);
      });
      return groups;
    },
    placeholderSampleMap() {
      const map = {};
      this.placeholderDefinitions.forEach((ph) => {
        map[ph.PlaceholderKey] = ph.SampleValue;
      });
      return map;
    },
    previewSubject() {
      return this.mergeTokens(
        this.escapeHtml(this.model.Subject || "(no subject yet)"),
      );
    },
    previewFileName() {
      return this.mergeTokens(
        this.escapeHtml(this.model.FileNameTemplate || "(no filename yet)"),
      );
    },
    previewBody() {
      return this.mergeTokens(
        this.model.Body ||
          '<span class="et-preview-placeholder">(no body yet)</span>',
      );
    },
    versionHistoryColumns() {
      return [
        {
          caption: "Version",
          dataField: "VersionNumber",
          width: 90,
          allowFiltering: false,
          allowSorting: false,
        },
        {
          caption: "Saved By",
          dataField: "CreatedBy",
          allowFiltering: false,
          allowSorting: false,
        },
        {
          caption: "Saved Date",
          dataField: "DateCreated",
          dataType: "date",
          format: "dd MMM yyyy H:mm aa",
          allowFiltering: false,
          allowSorting: false,
        },
      ];
    },
    versionHistoryDataSource() {
      const versions = (this.model.Versions || []).map((v) => ({ ...v }));
      versions.forEach((v) => {
        if (v.DateCreated) v.DateCreated = v.DateCreated.dateWithOffset();
      });
      return versions;
    },
  },
  watch: {
    toolBarKey() {
      // gc-form-header-toolbar is force-remounted via :key on this change,
      // so the previously-observed toolbar element is gone — reattach to
      // the new one.
      this.$nextTick(() => this.observeToolbarHeight());
    },
  },
  methods: {
    noop() {},
    toolbarformContentReady(e) {
      if (e && this.baseView.mode != "review" && this.loaded)
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("Name"))
            headerToolBarForm.getEditor("Name").instance().focus();
        }, 500);
    },
    placeholderToken(key) {
      return "{{" + key + "}}";
    },
    setField(field, value) {
      this.model[field] = value;
    },
    onDocumentTypeChanged(e) {
      this.model.EmailTemplateDocumentTypeRecordID = e.value;
      const option = this.documentTypeOptions.find(
        (o) => o.RecordId === e.value,
      );
      this.model.EmailTemplateDocumentType = option ? option.Label : "";

      // UC2: pre-fill Filename from the newly-selected document type's
      // DefaultFileNameTemplate — Create mode only, and only while Filename
      // is still empty; never overwrites a value the user already typed.
      if (
        this.baseView.mode === "create" &&
        !this.model.FileNameTemplate &&
        option
      ) {
        this.model.FileNameTemplate = option.DefaultFileNameTemplate || "";
      }

      this.loadPlaceholderDefinitions(e.value);
    },
    stripHtml(html) {
      const el = document.createElement("div");
      el.innerHTML = html || "";
      return el.textContent.trim();
    },
    /**
     * DxCustomRule callback for the Body field's DxValidator — a body of
     * "<p><br></p>" is non-empty as a string, so plain required validation
     * would pass it; strip the HTML and check for actual text instead.
     */
    validateBodyRequired(e) {
      return !!this.stripHtml(e.value);
    },
    // ResizeObserver-driven rather than a one-shot $nextTick read: on an
    // in-app Review -> Edit navigation the toolbar grows asynchronously
    // (Name/Document Type/Default/Active fields render in after mount), so
    // a single early measurement can capture a too-small toolbarHeight that
    // never gets corrected — leaving Body/Live Preview/Placeholders sized
    // larger than their row and silently clipped by it instead of
    // scrolling (Bug 77341). Observing keeps toolbarHeight accurate no
    // matter when the toolbar actually finishes rendering.
    observeToolbarHeight() {
      const toolbarEl = document.querySelector(".gc-header-toolbar");
      if (!toolbarEl) return;

      if (this.toolbarResizeObserver) this.toolbarResizeObserver.disconnect();

      this.toolbarHeight = toolbarEl.offsetHeight;
      this.toolbarResizeObserver = new ResizeObserver(() => {
        this.toolbarHeight = toolbarEl.offsetHeight;
      });
      this.toolbarResizeObserver.observe(toolbarEl);
    },
    observeAboveBodyFieldsHeight() {
      const aboveBodyFieldsEl = this.$refs["fields-above-body"];
      if (!aboveBodyFieldsEl) return;

      if (this.aboveBodyFieldsResizeObserver)
        this.aboveBodyFieldsResizeObserver.disconnect();

      this.aboveBodyFieldsHeight = aboveBodyFieldsEl.offsetHeight;
      this.aboveBodyFieldsResizeObserver = new ResizeObserver(() => {
        this.aboveBodyFieldsHeight = aboveBodyFieldsEl.offsetHeight;
      });
      this.aboveBodyFieldsResizeObserver.observe(aboveBodyFieldsEl);
    },
    focusNameField() {
      this.$nextTick(() => {
        const instance =
          this.$refs["name-textbox"] && this.$refs["name-textbox"].instance;
        if (instance) instance.focus();
      });
    },
    insertPlaceholder(key) {
      if (!this.editable) return;
      const token = this.placeholderToken(key);
      if (this.lastFocusedField === "Body") this.insertIntoBody(token);
      else if (this.lastFocusedField === "Filename")
        this.insertIntoFileName(token);
      else this.insertIntoSubject(token);
    },
    insertIntoSubject(token) {
      const instance =
        this.$refs["subject-textbox"] && this.$refs["subject-textbox"].instance;
      if (!instance) return;

      const inputEl = instance.element().querySelector("input");
      const current = this.model.Subject || "";
      const start =
        inputEl && inputEl.selectionStart != null
          ? inputEl.selectionStart
          : current.length;
      const end =
        inputEl && inputEl.selectionEnd != null ? inputEl.selectionEnd : start;

      this.model.Subject = current.slice(0, start) + token + current.slice(end);

      this.$nextTick(() => {
        instance.focus();
        if (inputEl) {
          const pos = start + token.length;
          inputEl.setSelectionRange(pos, pos);
        }
      });
    },
    insertIntoFileName(token) {
      const instance =
        this.$refs["filename-textbox"] &&
        this.$refs["filename-textbox"].instance;
      if (!instance) return;

      const inputEl = instance.element().querySelector("input");
      const current = this.model.FileNameTemplate || "";
      const start =
        inputEl && inputEl.selectionStart != null
          ? inputEl.selectionStart
          : current.length;
      const end =
        inputEl && inputEl.selectionEnd != null ? inputEl.selectionEnd : start;

      this.model.FileNameTemplate =
        current.slice(0, start) + token + current.slice(end);

      this.$nextTick(() => {
        instance.focus();
        if (inputEl) {
          const pos = start + token.length;
          inputEl.setSelectionRange(pos, pos);
        }
      });
    },
    insertIntoBody(token) {
      const editor = this.$refs["body-editor"];
      if (editor && editor.insertTextAtSelection) {
        editor.insertTextAtSelection(token);
      }
    },
    buildSavePayload(overrides) {
      return Object.assign(
        {
          RecordId: this.baseView.mode === "create" ? null : this.baseView.id,
          Name: this.model.Name,
          EmailTemplateDocumentTypeRecordId:
            this.model.EmailTemplateDocumentTypeRecordID,
          Subject: this.model.Subject,
          FileNameTemplate: this.model.FileNameTemplate,
          Body: this.model.Body,
          IsDefault: this.model.IsDefault ? "Y" : "N",
          Inactive: !this.model.IsActive ? "Y" : "N",
        },
        overrides,
      );
    },
    async saveTemplate() {
      let brokenRules = [];
      const headerToolbarForm = await validationEngine.validateGroup(
        "gc-form-header-toolbar",
      );

      if (!headerToolbarForm.isValid) {
        brokenRules = headerToolbarForm.brokenRules;
      }

      document.activeElement.blur();
      await this.$nextTick();
      const result = this.$refs.validationGroup.instance.validate();
      if (!result.isValid) {
        brokenRules = brokenRules.concat(
          result.brokenRules.map((rule) => ({ message: rule.message })),
        );
      }

      if (brokenRules.length > 0) {
        this.displayValidationSummary(brokenRules);
        return;
      }

      this.showLoading(true);
      await EmailTemplateService.save(this.buildSavePayload())
        .then((res) => {
          this.showLoading(false);
          this.setIgnoreRouteChangeConfirmation();
          this.$router
            .push({ path: `/email-template/review/${res.data.RecordId}` })
            .then(() => {
              this.loadTemplate();
            });
        })
        .catch((err) => {
          this.showLoading(false);
          if (err) this.displayServerError(err, "Save Failed");
        });
    },
    backButtonFunction() {
      this.$router.push({ path: this.backButtonRoute });
    },
    onTabItemClick(e) {
      this.activeTabIndex = e.itemIndex;

      setTimeout(() => {
        this.$nextTick(() => {
          this.filterFrom = "headerCollapse";

          var dataGrid = this.$refs["version-history-grid"].instance;
          if (dataGrid) {
            dataGrid.option("height", this.getGridHeight);
            dataGrid.repaint();
          }
        });
      }, 1);
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    mergeTokens(html) {
      const sample = this.placeholderSampleMap;
      // {{#if X}}...{{/if}} resolved before the flat token merge (technical-design.md
      // §7): keeps inner content when X has a SampleValue, otherwise removes the
      // whole block (markers and inner content) — single-level, non-nested, no else.
      const withConditionalsResolved = html.replace(
        /\{\{#if\s+([\w.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
        (match, key, inner) => (sample[key] !== undefined ? inner : ""),
      );
      return withConditionalsResolved.replace(
        /\{\{([a-zA-Z0-9_.]+)\}\}/g,
        (match, key) => {
          if (sample[key] !== undefined) {
            return `<span class="et-ph-valid">${this.escapeHtml(
              sample[key],
            )}</span>`;
          }
          return `<span class="et-ph-invalid">${this.escapeHtml(match)}</span>`;
        },
      );
    },
    async loadDocumentTypes() {
      await EmailTemplateService.getDocumentTypes()
        .then((res) => {
          this.documentTypeOptions = res.data.value || res.data || [];
        })
        .catch((err) => {
          if (err)
            this.displayServerError(err, "Failed to load document types");
        });
    },
    async loadPlaceholderDefinitions(documentTypeRecordId) {
      if (!documentTypeRecordId) {
        this.placeholderDefinitions = [];
        return;
      }

      await EmailTemplateService.getPlaceholderDefinitions(documentTypeRecordId)
        .then((phRes) => {
          this.placeholderDefinitions = phRes.data.value || phRes.data || [];
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Failed to load placeholders");
        });
    },
    async loadTemplate(refreshToolbar = false) {
      this.showLoading(true);
      await EmailTemplateService.getById(this.baseView.id)
        .then(async (res) => {
          const data = res.data;
          this.model = {
            ...this.model,
            RecordId: data.RecordId,
            Name: data.Name,
            EmailTemplateDocumentTypeRecordID:
              data.EmailTemplateDocumentTypeRecordID,
            EmailTemplateDocumentType: data.EmailTemplateDocumentType,
            Subject: data.Subject,
            FileNameTemplate: data.FileNameTemplate,
            Body: data.Body,
            IsDefault: data.IsDefault === "Y",
            IsActive: data.Inactive === "N",
            Versions: data.Versions || [],
            FriendlyStatus: data.Inactive === "Y" ? "Inactive" : "Active",
            DefaultLabel: "Default",
            CreatedBy: data.CreatedBy,
            DateCreated: data.DateCreated,
            ChangedBy: data.ChangedBy,
            DateChanged: data.DateChanged,
          };

          await this.loadPlaceholderDefinitions(
            data.EmailTemplateDocumentTypeRecordID,
          );

          this.showLoading(false);
          this.loaded = true;
        })
        .catch((err) => {
          if (err)
            this.displayServerError(err, "Failed to load email template");
          this.showLoading(false);
        });

      if (refreshToolbar) {
        this.$nextTick(() => {
          this.toolBarKey += 1;
        });
      }
    },
    async inactivateTemplate() {
      // BR-4: warn-not-block — advise reassigning the default first, but the
      // action proceeds regardless of the user's choice here.
      const message =
        this.model.IsDefault === "Y"
          ? `"${this.model.Name}" is currently the default template for its document/transaction type.<br />Consider assigning another template as the default before inactivating it. Inactivate anyway?`
          : `Are you sure you want to inactivate "${this.model.Name}"?`;

      let confirmInactivate = await this.$confirm(
        message,
        "Inactivate Email Template",
      );

      if (confirmInactivate) {
        this.showLoading(true);
        await EmailTemplateService.inactivate(this.baseView.id)
          .then(() => {
            this.loadTemplate(true);
          })
          .catch((err) => {
            if (err) this.displayServerError(err, "Inactivate Failed");
            this.showLoading(false);
          });
      }
    },
    async reactivateTemplate() {
      let confirmReactivate = await this.$confirm(
        `Are you sure you want to reactivate "${this.model.Name}"?`,
        "Reactivate Email Template",
      );

      if (!confirmReactivate) return;

      this.showLoading(true);
      await EmailTemplateService.reactivate(this.baseView.id)
        .then(async (res) => {
          await this.loadTemplate(true);

          // Server-driven: the endpoint's own check of whether an active
          // default still exists for this document/transaction type, not a
          // client-side guess. NoActiveDefaultExists only looks at OTHER
          // rows (technical-design.md), so skip the prompt when this
          // template was already the type's default — reactivating it made
          // it the active default again on its own, nothing to restore.
          if (
            res.data &&
            res.data.NoActiveDefaultExists &&
            this.model.IsDefault === "N"
          ) {
            await this.promptRestoreDefault();
          }
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Reactivate Failed");
          this.showLoading(false);
        });
    },
    async promptRestoreDefault() {
      const confirmDefault = await this.$confirm(
        `No active default template exists for "${this.model.EmailTemplateDocumentType}". Set "${this.model.Name}" as the new default?`,
        "Restore Default Template",
      );

      if (!confirmDefault) return;

      this.showLoading(true);
      await EmailTemplateService.save(this.buildSavePayload({ IsDefault: "Y" }))
        .then(() => {
          this.loadTemplate(true);
        })
        .catch((err) => {
          this.showLoading(false);
          if (err) this.displayServerError(err, "Set Default Failed");
        });
    },
  },
  async created() {
    await this.loadDocumentTypes();

    if (this.baseView.mode === "create") {
      this.loaded = true;
    } else {
      await this.loadTemplate();
    }

    this.$nextTick(() => {
      this.observeToolbarHeight();
      this.observeAboveBodyFieldsHeight();
    });

    if (this.editable) this.focusNameField();
  },
  beforeDestroy() {
    if (this.toolbarResizeObserver) this.toolbarResizeObserver.disconnect();
    if (this.aboveBodyFieldsResizeObserver)
      this.aboveBodyFieldsResizeObserver.disconnect();
  },
};
</script>

<style lang="scss">
.email-template.css-v2.transaction-screen {
  background-color: unset !important;
}
.email-template {
  .view-toolbar
    .dx-toolbar-before
    .dx-form
    .dx-item-content
    .dx-item.dx-box-item {
    width: 540px !important;
  }

  .view-toolbar .dx-toolbar-after .dx-form {
    .dx-scrollable-content {
      overflow: hidden !important;
    }
    .dx-item-content {
      .gc-form-header-toolbar-after-group-editor {
        .dx-item.dx-box-item {
          width: 190px !important;
        }
      }

      .gc-form-header-toolbar-after-group {
        .dx-item.dx-box-item {
          width: unset !important;
        }
      }
      .dx-item.dx-box-item {
        .field-checkbox-active {
          padding-left: 0 !important;
        }
      }
      .dx-first-row.dx-first-col.dx-field-item-optional.dx-field-item-has-group {
        padding-bottom: 0 !important;
      }
    }
  }
  .et-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .et-row-main {
    overflow: hidden;
  }

  .et-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    // Without an explicit row track, the single implicit row is sized
    // "auto" (max-content of its items), which makes height:100% on
    // .et-card/.et-right-col resolve as auto too (percentage heights don't
    // resolve against auto-sized grid tracks) — they silently grow to fit
    // their unclamped content instead of the row, and get clipped by
    // .et-row-main's overflow:hidden with no scrollbar (Bug 77341).
    // minmax(0, 1fr) makes the row track — and its automatic minimum size —
    // definite, so the two columns actually stretch to the row's height.
    grid-template-rows: minmax(0, 1fr);
    gap: 0 12px;
    // No align-items: start here — grid children must stretch to fill the
    // row's full height, or Body/Live Preview only size to their content
    // and leave blank space below them.
    height: 100%;
  }

  .et-card {
    background: #fff;
    border: 1px solid #d0d5dd;
    padding: 16px;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .et-field {
    margin-bottom: 16px;

    label {
      display: block;
      font-size: 11px;
      color: #667085;
      margin-bottom: 4px;
    }

    .et-value {
      font-size: 12px;
      color: #323842;
      padding-bottom: 5px;
      border-bottom: 1px dashed #d0d5dd;
      min-height: 18px;
    }
  }

  // Body's wrapper grows to fill whatever space Subject leaves within the
  // card — the actual DevExtreme editor already ships with its own internal
  // scrolling (.dx-htmleditor-content is overflow-y: auto by default), it
  // just needs a real resolved height to size against.
  .et-field.et-field-grow {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0; // lets this flex item shrink below its content size —
    // without it, height:100% below has nothing to resolve against

    label {
      flex: 0 0 auto;
    }

    .gc-html-editor {
      flex: 1;
      min-height: 0;
    }
  }

  .et-field.et-field-checkbox {
    display: flex;
    align-items: center;
    align-items: center;

    .et-value {
      margin-right: 8px;
      padding-bottom: 0;
      border-bottom: none;
    }

    label {
      margin-bottom: unset !important;
      font-size: 12px;
    }
  }

  .et-right-col {
    height: 100%;
    display: flex;
    flex-direction: column;

    .et-preview-pane {
      background: #fbfbfc;
      border: 1px solid #d0d5dd;
      padding: 14px;
      box-sizing: border-box;
      flex: 1;
      min-height: 0; // fills the row's full height as a minimum — only
      overflow-y: auto; // scrolls internally once content exceeds that

      .et-preview-label {
        font-size: 10px;
        // text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #667085;
        margin-bottom: 4px;
        font-weight: 600;
      }

      .et-preview-subject {
        font-size: 13px;
        font-weight: 600;
        color: #101828;
        margin-bottom: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e0e0e0;
      }

      .et-preview-filename {
        font-size: 12px;
        font-family: monospace;
        color: #475467;
        margin-bottom: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e0e0e0;
      }

      .et-preview-body {
        font-size: 12px;
        line-height: 1.6;
        overflow: auto;

        p {
          margin: 4px 0;
        }
      }
    }
  }

  // Row 2: Placeholders / Version History, below the Subject/Body + Preview
  // row (previously stacked inside .et-right-col alongside the preview).
  .et-row-secondary {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .et-tab-panel {
      flex: 1;
      overflow-y: auto;
      // padding-top: 12px;
      .dx-datagrid-header-panel {
        display: none;
      }
    }

    .et-tab-panel.placeholder-tab {
      padding-top: 12px;
      background-color: white;
      .et-ph-group {
        padding: 2px 10px 0px 10px;
      }

      .et-ph-group:last-child {
        padding-bottom: 15px;
      }
    }

    .et-ph-group {
      margin-bottom: 5px;

      .et-ph-group-title {
        font-size: 12px;
        // text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #667085;
        font-weight: 600;
        margin-bottom: 6px;
      }

      .et-ph-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .et-ph-chip {
        display: inline-block;
        background: #f2f4f7;
        border: 1px solid #d0d5dd;
        border-radius: 4px;
        padding: 3px 8px;
        font-size: 11px;
        color: #475467;
        font-family: monospace;
        cursor: default;
      }

      .et-ph-chip-clickable {
        cursor: pointer;
        background: #eef8fd;
        border-color: #bfe6f7;
        color: #2a95c5;

        &:hover {
          background: #dcf0fa;
        }
      }
    }
  }

  .dx-invalid-message > .dx-overlay-content {
    color: #fff;
    background-color: #d9534f;
    // border-radius: 4px;
    display: inline-block;
    border-width: 0;
    padding: 8px;
    font-size: 0.85em;
    line-height: normal;
    word-wrap: break-word;
  }
}

.et-ph-valid {
  color: #2a95c5;
  font-weight: 600;
}

.et-ph-invalid {
  color: #e8384f;
  font-weight: 600;
  background: #fdecec;
  padding: 0 2px;
  border-radius: 2px;
}

.et-preview-placeholder {
  color: #667085;
}
</style>
